import { useEffect, useRef } from "react";
import type { Order } from "../../lib/types.ts";
import { isComplete } from "../../lib/status.ts";
import { hatRecht } from "../../state/berechtigungen.ts";

/**
 * Automatischer Archivierungs-Sweep (Task 1218053584680825, 2026-09-01): bezahlt und Dokumente
 * vollstaendig (`isComplete`) ist eine Hub-Regel, unabhaengig vom JTL-Status. „Dokumente vollstaendig"
 * urteilt seit Spec 2026-10-31-board-dokumentstand der Server (`dokumentStand`, wie die Checkliste).
 * `edit_orders` schuetzt denselben Schreibweg wie die Drawer-Fussleiste: `drawerAendern`.
 * Der Hintergrundaufruf schliesst keinen Drawer und zeigt keinen Erfolgs-Toast; Schreibfehler
 * meldet der bestehende Kanal selbst - im Fehlerstapel, mit der Bestellung im Text, weil der Mensch die
 * Archivierung nicht angestossen hat (`useDrawerBestellfelder#drawerAendern`, `hintergrund`). Kein automatischer Retry in derselben Sitzung.
 *
 * Nur `false -> true` archiviert; eine erstmalig geladene vollstaendige Bestellung zaehlt als
 * Uebergang. Auch archivierte Bestellungen werden beobachtet: Wiederherstellen aendert nur
 * `archived` und darf deshalb nicht sofort wieder archivieren. Die Merkliste wird vor dem
 * Schreiben aktualisiert und verhindert auch den StrictMode-Doppelaufruf. Angestossene IDs
 * bleiben als zusaetzlicher Schutz bis zum Neuladen erhalten, auch nach erneutem `false -> true`.
 */
export function useAutoArchivierung(
  bestellungen: readonly Order[],
  rechte: ReadonlySet<string>,
  drawerAendern: (id: string, aenderung: Record<string, unknown>) => void,
): void {
  const archivierungAngestossen = useRef<Set<string>>(new Set());
  const vorherVollstaendigRef = useRef<Map<string, boolean>>(new Map());
  useEffect(() => {
    if (!hatRecht(rechte, "edit_orders")) return;
    for (const bestellung of bestellungen) {
      const id = bestellung.id as string;
      const jetzt = isComplete(bestellung);
      const vorher = vorherVollstaendigRef.current.get(id) ?? false;
      // Auch archivierte Bestellungen beobachten, damit Wiederherstellen kein neuer Uebergang ist.
      vorherVollstaendigRef.current.set(id, jetzt);
      const geradeVollstaendigGeworden = jetzt && !vorher;
      if (!geradeVollstaendigGeworden) continue;
      if (bestellung.archived) continue;
      if (archivierungAngestossen.current.has(id)) continue;
      archivierungAngestossen.current.add(id);
      drawerAendern(id, { archived: true });
    }
  }, [bestellungen, rechte]);
}
